import { Check, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { Locale } from "@mazzager/contracts";

const CANVAS = 320;
const FRAME = 260;

function clamp(image: HTMLImageElement, zoom: number, x: number, y: number) {
  const safeZoom = Math.max(1, Math.min(5, zoom));
  const base = Math.max(FRAME / image.naturalWidth, FRAME / image.naturalHeight);
  const maxX = Math.max(0, (image.naturalWidth * base * safeZoom - FRAME) / 2);
  const maxY = Math.max(0, (image.naturalHeight * base * safeZoom - FRAME) / 2);
  return { zoom: safeZoom, x: Math.max(-maxX, Math.min(maxX, x)), y: Math.max(-maxY, Math.min(maxY, y)) };
}

export function AvatarCropDialog({ file, locale, saving, onCancel, onSave }: { file: File; locale: Locale; saving: boolean; onCancel: () => void; onSave: (blob: Blob) => void }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const previousGesture = useRef<{ x: number; y: number; distance: number } | null>(null);
  const [ready, setReady] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [transform, setTransform] = useState({ zoom: 1, x: 0, y: 0 });
  const ru = locale === "ru";

  useEffect(() => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => { imageRef.current = image; setReady(true); };
    image.onerror = onCancel;
    image.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file, onCancel]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const image = imageRef.current;
    if (!canvas || !image || !ready) return;
    const ratio = window.devicePixelRatio || 1;
    canvas.width = CANVAS * ratio;
    canvas.height = CANVAS * ratio;
    const context = canvas.getContext("2d");
    if (!context) return;
    context.setTransform(ratio, 0, 0, ratio, 0, 0);
    context.clearRect(0, 0, CANVAS, CANVAS);
    const base = Math.max(FRAME / image.naturalWidth, FRAME / image.naturalHeight);
    const scale = base * transform.zoom;
    const width = image.naturalWidth * scale;
    const height = image.naturalHeight * scale;
    context.drawImage(image, (CANVAS - width) / 2 + transform.x, (CANVAS - height) / 2 + transform.y, width, height);
    context.fillStyle = "rgba(0,0,0,.62)";
    context.beginPath();
    context.rect(0, 0, CANVAS, CANVAS);
    context.arc(CANVAS / 2, CANVAS / 2, FRAME / 2, 0, Math.PI * 2, true);
    context.fill("evenodd");
    context.strokeStyle = "#21df79"; context.lineWidth = 3; context.beginPath(); context.arc(CANVAS / 2, CANVAS / 2, FRAME / 2, 0, Math.PI * 2); context.stroke();
    if (dragging) {
      context.save(); context.beginPath(); context.arc(CANVAS / 2, CANVAS / 2, FRAME / 2, 0, Math.PI * 2); context.clip();
      context.strokeStyle = "rgba(255,255,255,.42)"; context.lineWidth = 1;
      for (let index = 1; index < 3; index++) {
        const value = (CANVAS - FRAME) / 2 + FRAME * index / 3;
        context.beginPath(); context.moveTo(value, (CANVAS - FRAME) / 2); context.lineTo(value, (CANVAS + FRAME) / 2); context.stroke();
        context.beginPath(); context.moveTo((CANVAS - FRAME) / 2, value); context.lineTo((CANVAS + FRAME) / 2, value); context.stroke();
      }
      context.restore();
    }
  }, [dragging, ready, transform]);

  const gesture = () => {
    const points = [...pointers.current.values()];
    if (!points.length) return null;
    const x = points.reduce((sum, point) => sum + point.x, 0) / points.length;
    const y = points.reduce((sum, point) => sum + point.y, 0) / points.length;
    const distance = points.length > 1 ? Math.hypot(points[0]!.x - points[1]!.x, points[0]!.y - points[1]!.y) : 0;
    return { x, y, distance };
  };

  const movePointer = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!pointers.current.has(event.pointerId)) return;
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const current = gesture(); const previous = previousGesture.current; const image = imageRef.current;
    if (!current || !previous || !image) { previousGesture.current = current; return; }
    const zoom = current.distance > 0 && previous.distance > 0 ? transform.zoom * current.distance / previous.distance : transform.zoom;
    setTransform(clamp(image, zoom, transform.x + current.x - previous.x, transform.y + current.y - previous.y));
    previousGesture.current = current;
  };

  const releasePointer = (event: React.PointerEvent<HTMLCanvasElement>) => {
    pointers.current.delete(event.pointerId); previousGesture.current = gesture(); if (!pointers.current.size) setDragging(false);
  };

  const save = async () => {
    const image = imageRef.current; if (!image) return;
    const output = document.createElement("canvas"); output.width = 512; output.height = 512;
    const context = output.getContext("2d"); if (!context) return;
    const base = Math.max(FRAME / image.naturalWidth, FRAME / image.naturalHeight);
    const scale = base * transform.zoom * 512 / FRAME;
    const width = image.naturalWidth * scale; const height = image.naturalHeight * scale;
    context.drawImage(image, (512 - width) / 2 + transform.x * 512 / FRAME, (512 - height) / 2 + transform.y * 512 / FRAME, width, height);
    const blob = await new Promise<Blob | null>((resolve) => output.toBlob(resolve, "image/webp", .9))
      ?? await new Promise<Blob | null>((resolve) => output.toBlob(resolve, "image/jpeg", .9));
    if (blob) onSave(blob);
  };

  return <div className="avatar-crop-backdrop" role="dialog" aria-modal="true" aria-label={ru ? "Кадрирование аватарки" : "Crop avatar"}>
    <div className="avatar-crop-dialog">
      <header><button type="button" onClick={onCancel} disabled={saving} aria-label={ru ? "Закрыть" : "Close"}><X /></button><div><h3>{ru ? "Новая аватарка" : "New avatar"}</h3><span>{ru ? "Расположите фото внутри круга" : "Position the photo inside the circle"}</span></div></header>
      <canvas
        ref={canvasRef}
        className="avatar-crop-canvas"
        onDoubleClick={() => setTransform({ zoom: 1, x: 0, y: 0 })}
        onWheel={(event) => { event.preventDefault(); const image = imageRef.current; if (image) setTransform(clamp(image, transform.zoom * Math.exp(-event.deltaY * .002), transform.x, transform.y)); }}
        onPointerDown={(event) => { event.currentTarget.setPointerCapture(event.pointerId); pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY }); previousGesture.current = gesture(); setDragging(true); }}
        onPointerMove={movePointer}
        onPointerUp={releasePointer}
        onPointerCancel={releasePointer}
      />
      {!ready && <div className="avatar-crop-loading"><span className="spinner" /></div>}
      <div className="avatar-crop-actions"><button type="button" onClick={onCancel} disabled={saving}>{ru ? "Отмена" : "Cancel"}</button><button type="button" className="primary" onClick={() => void save()} disabled={!ready || saving}>{saving ? <span className="spinner" /> : <Check size={18} />}{ru ? "Сохранить" : "Save"}</button></div>
    </div>
  </div>;
}
